import { useEffect, type ReactNode } from 'react';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type ActionModalTone = 'amber' | 'red';

interface ActionModalProps {
    open: boolean;
    tone: ActionModalTone;
    title: string;
    subtitle: string;
    /** Callout shown below the body. */
    helperText: string;
    submitLabel: string;
    canSubmit: boolean;
    isSubmitting: boolean;
    onSubmit: () => void;
    onClose: () => void;
    children: ReactNode;
}

/** Dialog chrome for a workflow action that captures input before sending the case back or ending it. */
export function ActionModal({
    open,
    tone,
    title,
    subtitle,
    helperText,
    submitLabel,
    canSubmit,
    isSubmitting,
    onSubmit,
    onClose,
    children
}: ActionModalProps) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape' && !isSubmitting) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, isSubmitting, onClose]);

    if (!open) return null;

    const accent = TONE_STYLES[tone];

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reason-modal-title"
            onClick={(e) => e.target === e.currentTarget && !isSubmitting && onClose()}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
        >
            <div className="w-full max-w-[500px] bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-200 flex items-center gap-3">
                    <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}
                    >
                        <ArrowPathIcon className={`w-4 h-4 ${accent.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div id="reason-modal-title" className="text-sm font-bold text-gray-900">
                            {title}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        aria-label="Close"
                        className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 flex flex-col gap-3">
                    {children}
                    <div
                        className={`text-xs leading-relaxed rounded-md px-3 py-2 border ${accent.helperBg}`}
                    >
                        {helperText}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => canSubmit && onSubmit()}
                        disabled={!canSubmit}
                        className={`px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 ${accent.submitBg}`}
                    >
                        {isSubmitting && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                        {submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const TONE_STYLES: Record<
    ActionModalTone,
    {
        iconBg: string;
        iconColor: string;
        helperBg: string;
        submitBg: string;
    }
> = {
    amber: {
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-700',
        helperBg: 'text-gray-700 bg-yellow-50 border-yellow-200',
        submitBg: 'bg-yellow-400 text-gray-900 hover:bg-yellow-500'
    },
    red: {
        iconBg: 'bg-red-100',
        iconColor: 'text-red-700',
        helperBg: 'text-red-800 bg-red-50 border-red-200',
        submitBg: 'bg-red-600 text-white hover:bg-red-700'
    }
};
